/**
 * 学習ログの起動判定と 15 秒周期チェッカー（08_client-state-machine.md §7）。
 *
 * マウント要素（[data-lms-learning-log]）の有無は毎 tick 判定する（起動時の 1 回だけではない）。
 * 部分更新遷移（ADR-0011）で JS 実行コンテキストと setInterval が生存し続けるため、
 * 起動時判定だと非学習ページへ遷移した後もタイマーが動き続けてしまう（§7.1）。
 */
import { fetchToday, postLearningLog } from "./api.js";
import { readPayload, syncToast } from "./dom.js";
import {
  decide,
  isToastVisible,
  onConfirmClicked,
  onSendFailed,
  onSendSucceeded,
  onTodayFetched,
  TICK_INTERVAL_MS,
  type LearningLogState,
  type SendReason,
} from "./state.js";
import { loadState, saveState } from "./storage.js";

/** POST の応答待ち中は true。重複送信を防ぐ（06_error-handling.md C-13）。 */
let sendInFlight = false;

/** GET の応答待ち中に、その要求を出した時点の pendingCheckStartedAt を保持する。待機中でなければ null。 */
let todayInFlightFor: number | null = null;

/**
 * この JS コンテキストで setInterval を張り済みか。
 * 部分更新遷移（ADR-0011）で JS が生存したまま initLearningLog が複数回呼ばれても
 * タイマーを 1 本に保つためのガード（§1 原則 1・§7.1）。
 */
let timerStarted = false;

function handleConfirmClick(): void {
  const now = Date.now();
  const stored = loadState();
  if (stored === null) {
    syncToast(false, handleConfirmClick);
    return;
  }
  const { state, effects } = onConfirmClicked(stored, now);
  saveState(state);
  syncToast(isToastVisible(state, now), handleConfirmClick);
  const sendEffect = effects.find((effect) => effect.type === "SEND_LOG");
  if (sendEffect && sendEffect.type === "SEND_LOG") {
    void send(sendEffect.reason);
  }
}

/** POST /api/learning-log を送信し、結果を状態に反映する（§7.4）。 */
async function send(reason: SendReason): Promise<void> {
  if (sendInFlight) return; // 06_error-handling.md C-13

  const payload = readPayload();
  if (payload === null) {
    console.error("learning-log: curriculumId を取得できないため送信しません");
    return; // 04_sequence.md 1-6: 状態は変更せず onSendFailed も呼ばない
  }

  sendInFlight = true;
  const success = await postLearningLog(payload);
  const now2 = Date.now();
  const latest = loadState();
  if (latest === null) {
    // 日付リセット済み
    sendInFlight = false;
    return;
  }

  let nextState: LearningLogState;
  if (success) {
    nextState = onSendSucceeded(latest, now2);
  } else {
    nextState = onSendFailed(latest, now2);
    console.debug(`learning-log: 送信に失敗しました（reason=${reason}）。1 分後に再送します`);
  }
  saveState(nextState);
  sendInFlight = false;
  syncToast(isToastVisible(nextState, now2), handleConfirmClick);
}

/** GET /api/learning-log/today を取得し、結果を状態に反映する（§7.3 手順 6.1）。 */
async function handleFetchToday(stateAtRequestTime: LearningLogState): Promise<void> {
  if (todayInFlightFor !== null) return; // 04_sequence.md 2-3: 重複呼び出し防止

  todayInFlightFor = stateAtRequestTime.pendingCheckStartedAt;
  const hasLogToday = await fetchToday();
  const now2 = Date.now();
  const latest = loadState();

  if (latest === null || latest.pendingCheckStartedAt !== todayInFlightFor) {
    // 日付リセット・フェーズ進行の後に届いた遅延応答は破棄する（04_sequence.md 2-7）。
    todayInFlightFor = null;
    return;
  }

  saveState(onTodayFetched(latest, hasLogToday, now2));
  todayInFlightFor = null;
  // 予定時刻を既に過ぎていた場合に積み残さないよう、続けて tick をもう一度呼ぶ（04_sequence.md 2-5）。
  await tick();
}

/** 15 秒周期チェッカーの本体（§7.3）。 */
async function tick(): Promise<void> {
  const mount = document.querySelector<HTMLElement>("[data-lms-learning-log]");
  if (mount === null) {
    // ADMIN・非学習ページ、または部分更新遷移で学習画面から離れた場合。
    // 状態機械を凍結し、残存トーストだけを消す（LL-R1）。
    syncToast(false, handleConfirmClick);
    return;
  }

  const now = Date.now();
  const stored = loadState();
  const { state, effects } = decide(stored, now);
  saveState(state);
  syncToast(isToastVisible(state, now), handleConfirmClick);

  for (const effect of effects) {
    if (effect.type === "FETCH_TODAY") {
      await handleFetchToday(state);
    } else {
      await send(effect.reason);
    }
  }
}

/**
 * 学習ログ機能を起動する。
 *
 * 15 秒周期チェッカーを 1 本だけ起動する（timerStarted ガード）。実際に確認トースト・API を動かすかは
 * 毎 tick で [data-lms-learning-log] の有無を見て決める（§7.3）。ADMIN には Thymeleaf 側でこの要素を
 * 描画しないため、周期チェッカーが動いても何も起こさない（spec.md LL-R1）。
 * 部分更新遷移（ADR-0011）で複数回呼ばれても、既にタイマーがあれば即時 tick だけを行う。
 */
export function initLearningLog(): void {
  if (timerStarted) {
    void tick();
    return;
  }
  timerStarted = true;
  void tick();
  window.setInterval(() => {
    void tick();
  }, TICK_INTERVAL_MS);
}
